import { AuthorizationTypes } from '@site-builder/common/src/types/model/landing';

import type { UniversalItem } from '../../actions/buyUniversalItem';
import type { ScriptCommonArgs } from '../../types';
import type { Authorization } from '@site-builder/common/src/types/model/landing';

import { isCustomAuth } from '../../../experiments/partnersRequests/utils';
import { ScriptsState } from '../../../reducers/types/scripts-state';
import { documentClickListener } from '../../../utils/document-listener';
import { VC_TYPE } from '../../../utils/store-helper';
import { handleUniversalItem } from '../../actions/buyUniversalItem';
import { analyticsManager } from '../../afterBlocks/analytics/analytics';
import { AnalyticsActions } from '../../afterBlocks/analytics/analyticsActions';
import { AnalyticsCategories } from '../../afterBlocks/analytics/analyticsCategories';
import { showNotification } from '../../helpers/common';
import { getStore } from '../../store';
import { translations } from '../../translations';
import {
  openLoginWidget,
  openUserIdModalForButton,
} from '../user-account-services';
import { UserAccountLoginService } from '../user-account-services/user-account-login-service';
import { buyItemForVirtualCurrency } from './api';
import { closeBundleInfoModal } from './bundle-modal';

const buyForVirtualCurrency = ({ userToken }: { userToken?: string }) => {
  const store = getStore();
  const {
    landingServices: { userService },
    scripts: { projectId },
  } = store.getState();

  documentClickListener.addListener({
    selector: `.store__buy-btn[data-currency-type="${VC_TYPE}"]`,
    handle: async (element) => {
      if (!userToken) {
        throw Error('userToken is undefined');
      }
      element.classList.add('store__buy-btn--is-loading');
      const virtualItemsSku = element.getAttribute('data-sku') as string;
      const virtualCurrencySku = element.getAttribute('data-vc') as string;

      const result = await buyItemForVirtualCurrency(
        userToken,
        projectId,
        virtualItemsSku,
        virtualCurrencySku
      );
      element.classList.remove('store__buy-btn--is-loading');

      if (result.order_id) {
        showNotification(
          translations['client.store.successful_purchase'],
          'success'
        );
        (userService as UserAccountLoginService).updateBalance();
      }

      if (!result.errorCode) {
        return;
      }

      switch (result.errorCode) {
        case 5006:
          showNotification(translations['client.store.not_enough_vc'], 'error');
          break;
        case 1422:
          showNotification(
            translations['client.store.non_consumable_item'],
            'error'
          );
          break;
        default:
          showNotification(translations['client.common_error'], 'error');
          break;
      }
    },
  });
};

const buyForRealPrice = ({
  isAuth,
  buttons,
  data,
  block,
  translations,
  storeAPIDataService,
}: {
  isAuth: boolean;
  buttons: HTMLElement[];
  data: ScriptsState;
  block: HTMLDivElement;
  translations: any;
  storeAPIDataService: any;
}) => {
  const selector = `.store__buy-btn[data-currency-type="real"]${
    !isAuth ? '[data-require-auth="false"]' : ''
  }`;
  documentClickListener.addListener({
    selector,
    handle: (button) => {
      analyticsManager.sendEvent({
        category: AnalyticsCategories.STORE,
        event: AnalyticsActions.CLICK_BUY,
        page: `landing_${AnalyticsActions.CLICK_BUY}`,
        label: button.getAttribute('data-sku') as string,
      });
      const sku = button.getAttribute('data-sku') as string;
      closeBundleInfoModal(sku);
    },
  });

  buttons
    .filter((button) => {
      const type = button.getAttribute('data-currency-type');
      const isRequireAuth = button.getAttribute('data-require-auth');
      return type === 'real' && (isAuth || isRequireAuth === 'false');
    })
    .forEach((button: HTMLElement) => {
      const universalItem = {
        sku: button.getAttribute('data-sku'),
        drm: null,
        projectId: null,
        target: `#${button.getAttribute('id') || ''}`,
        type: button.getAttribute('data-item-type'),
        isPreOrder: button.getAttribute('data-is-pre-order') === 'true',
      } as UniversalItem;

      handleUniversalItem({
        data,
        universalItem,
        block,
        translations,
        storeAPIDataService,
        requiresBuyButtonWidget: !isAuth,
      });
    });
};

export const setupSectionCardsHandlers = ({
  buttons,
  data,
  block,
  auth,
  userToken,
}: {
  buttons: HTMLElement[];
  data: ScriptCommonArgs;
  block: HTMLDivElement;
  auth: Authorization;
  userToken: string;
}) => {
  const store = getStore();
  const { landingServices } = store.getState();
  const { storeAPIDataService } = landingServices;

  const isAuth = !!userToken;
  if (isAuth) {
    buyForRealPrice({
      buttons,
      data,
      block,
      translations,
      storeAPIDataService,
      isAuth,
    });
    buyForVirtualCurrency({ userToken });
  } else if (auth.type === AuthorizationTypes.USER_ID) {
    const selector = `.store__buy-btn[data-currency-type="real"][data-require-auth="true"]`;
    documentClickListener.addListener({
      selector,
      handle: (button) => {
        const buyItem = button.closest('[data-buy-button-item]');
        openUserIdModalForButton(buyItem?.id);
      },
    });
  } else {
    documentClickListener.addListener({
      selector: '[data-require-auth="true"]',
      handle: (button: Element) => {
        analyticsManager.sendEvent({
          category: AnalyticsCategories.STORE,
          event: AnalyticsActions.CLICK_BUY,
          page: `landing_store_${AnalyticsActions.CLICK_BUY}`,
          label: button.getAttribute('data-sku') as string,
        });
        analyticsManager.sendEvent({
          category: AnalyticsCategories.STORE,
          event: AnalyticsActions.OPEN_LOGIN,
          page: `landing_${AnalyticsActions.OPEN_LOGIN}`,
        });
      },
    });
    const loginButtons = buttons.filter(
      (btn) => btn.getAttribute('data-require-auth') === 'true'
    );
    openLoginWidget({
      isCustomAuth: isCustomAuth(),
      loginButtons,
      sourceFinder: (btn) => {
        const buyItem = btn.closest('[data-buy-button-item]');
        return buyItem ? buyItem.id : null;
      },
    });
    buyForRealPrice({
      buttons,
      block,
      isAuth,
      data,
      storeAPIDataService,
      translations,
    });
  }
};
